import { ExternalLink, Globe } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { webUrl } from '@/lib/mobileBuild';

/**
 * Shown on the mobile app in place of a digital-goods Stripe checkout.
 * Directs the user to complete the purchase on the website (keeps the app
 * compliant with Google Play / Apple in-app-purchase policies).
 *
 * When a real web URL is resolvable (REACT_APP_WEB_URL is set), a QR code is
 * rendered so the user can scan it from another device (or share to desktop)
 * and land on the exact same path — reducing drop-off in the cross-device flow.
 * Pass showQr={false} on small modal surfaces that can't fit it.
 */
const UpgradeOnWebNotice = ({
  title = 'Upgrade on our website',
  description = 'Digital purchases are handled securely on our website. Visit us to upgrade, then come back to enjoy it here.',
  ctaLabel = 'Visit Website',
  path = '',
  testId = 'upgrade-on-web',
  showQr = true,
}) => {
  const href = webUrl(path);
  const hasUrl = href && href !== '#';
  return (
    <div
      className="rounded-xl border-2 border-cyan-500/40 bg-cyan-50/70 p-6 text-center"
      data-testid={testId}
    >
      <div
        className="inline-flex items-center justify-center w-12 h-12 rounded-full mb-3 bg-cyan-100"
      >
        <Globe className="w-6 h-6 text-cyan-700" />
      </div>
      <h3 className="text-lg font-bold mb-2" style={{ color: '#331a67' }}>{title}</h3>
      {description && <p className="text-sm text-gray-600 mb-5">{description}</p>}
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-cyan-600 hover:bg-cyan-700 text-white font-semibold transition-colors"
        data-testid={`${testId}-link`}
      >
        {ctaLabel} <ExternalLink className="w-4 h-4" />
      </a>
      {showQr && hasUrl && (
        <div className="mt-5 flex flex-col items-center" data-testid={`${testId}-qr`}>
          <div className="bg-white p-3 rounded-lg shadow-sm border border-cyan-200">
            <QRCodeSVG value={href} size={120} level="M" includeMargin={false} />
          </div>
          <p className="text-xs text-gray-500 mt-2">Scan to continue on another device</p>
        </div>
      )}
    </div>
  );
};

export default UpgradeOnWebNotice;
